import type { ContactWithApps } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { patch } from '../api/client';
import { keys, refreshSummaries, useContacts } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Avatar, Empty, Icon, PageHead } from '../components/ui';
import { ContactButtons, ContactDetails, ContactFields } from '../panel/Contacts';

function ContactCard({ c }: { c: ContactWithApps }) {
  const ui = useUI();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const save = async (f: Partial<ContactWithApps>) => {
    const cur = qc
      .getQueriesData<ContactWithApps[]>({ queryKey: ['contacts'] })
      .flatMap(([, l]) => l ?? [])
      .find((x) => x.id === c.id);
    await patch(`/contacts/${c.id}`, { ...f, version: cur?.version ?? c.version });
    await qc.invalidateQueries({ queryKey: ['contacts'] });
    for (const a of c.applications) void qc.invalidateQueries({ queryKey: keys.app(a.id) });
    void qc.invalidateQueries({ queryKey: keys.apps() });
    refreshSummaries(qc);
  };
  return (
    <div className="ccard">
      <div className="who">
        <Avatar name={c.name || c.email} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{c.name || c.email || 'Без имени'}</b>
          <span>{c.role || 'Контакт'}</span>
        </div>
        <button className="icon-btn" onClick={() => setEdit(!edit)} aria-label="Редактировать контакт">
          <Icon name={edit ? 'check' : 'pencil'} size={16} />
        </button>
      </div>
      {c.applications.map((a) => (
        <button
          key={a.id}
          className="tmain clickable"
          style={{ borderRadius: 8, padding: 6, margin: '-6px' }}
          onClick={() => ui.openApp(a.id)}
        >
          <span className="capp">
            {a.company} · {a.role || 'роль не указана'}
          </span>
        </button>
      ))}
      {edit ? (
        <ContactFields c={c} onSave={save} />
      ) : (
        <>
          <ContactDetails c={c} />
          <div className="btnrow">
            <ContactButtons c={c} />
          </div>
        </>
      )}
    </div>
  );
}

export function ContactsPage() {
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const all = useContacts('');
  const list = useContacts(dq);
  const head = <PageHead title="Контакты" sub="HR, рекрутеры и нанимающие менеджеры" />;
  const visible = (list.data ?? []).filter((c) => c.name || c.email || c.linkedinUrl || c.telegram);
  if (all.isPending) return head;
  if (!(all.data ?? []).some((c) => c.name || c.email || c.linkedinUrl || c.telegram))
    return (
      <>
        {head}
        <Empty
          title="Контактов пока нет"
          text="Добавьте контакт в карточке вакансии или разберите письмо рекрутера — контакт заполнится сам."
        />
      </>
    );
  return (
    <>
      {head}
      <label className="search" style={{ marginBottom: 14 }}>
        <Icon name="search" size={20} />
        <input
          type="search"
          placeholder="Имя, компания, email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Поиск контактов"
        />
      </label>
      <div className="cgrid">
        {visible.map((c) => (
          <ContactCard key={c.id} c={c} />
        ))}
      </div>
      {!visible.length && <p className="muted">Ничего не найдено.</p>}
    </>
  );
}
